import {
  Organization,
  OrganizationCreateInput,
  OrganizationCreateMutation,
  OrganizationGetQuery,
  OrganizationListQuery,
  OrganizationUpdateInput,
  OrganizationUpdateMutation,
  OrganizationGetMeQuery,
  UserResendInviteMutation,
  UserDeleteMutation,
  OrganizationInviteUserMutation,
  UserInviteInput
} from '@/API';
import {
  organizationCreate,
  organizationUpdate,
  userResendInvite,
  userDelete,
  organizationInviteUser
} from '@/graphql/mutations';
import { organizationGet, organizationList, organizationGetMe } from '@/graphql/queries';
import { listOrganizationUsersFilteredLite as listOrganizationUsersFilteredQuery } from '@/graphql/queries.custom';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';

const client = generateClient();

export async function createOrganization(input: OrganizationCreateInput) {
  const session = await client.graphql<GraphQLQuery<OrganizationCreateMutation>>({
    query: organizationCreate,
    variables: { input }
  });

  const result = (session as any)?.data?.organizationCreate;
  if (!result) {
    const message = session?.errors?.[0]?.message ?? 'Unable to create organization';
    throw new Error(message);
  }

  return result;
}

export async function updateOrganization(input: OrganizationUpdateInput) {
  const session = await client.graphql<GraphQLQuery<OrganizationUpdateMutation>>({
    query: organizationUpdate,
    variables: {
      id: input.id,
      input: input
    }
  });

  const result = (session as any)?.data?.organizationUpdate;
  if (!result) {
    const message = session?.errors?.[0]?.message ?? 'Unable to update organization';
    throw new Error(message);
  }

  return result;
}

export async function getOrganization(id: string) {
  const session = await client.graphql<GraphQLQuery<OrganizationGetQuery>>({
    query: organizationGet,
    variables: { id }
  });

  return session.data?.organizationGet;
}

export async function getMyOrganization() {
  const session = await client.graphql<GraphQLQuery<OrganizationGetMeQuery>>({
    query: organizationGetMe
  });

  return session.data?.organizationGetMe as Organization;
}

export async function listOrganizations() {
  const session = await client.graphql<GraphQLQuery<OrganizationListQuery>>({
    query: organizationList
  });

  return session.data?.organizationList;
}

export async function listOrganizationsPublic() {
  const session = await client.graphql({
    query: /* GraphQL */ `
      query OrganizationPublicList {
        organizationPublicList {
          id
          name
          subdomain
          tenant
          __typename
        }
      }
    `,
    authMode: 'none'
  });

  return (
    (
      session as unknown as {
        data?: { organizationPublicList?: { id: string; name: string; tenant: string }[] };
      }
    ).data?.organizationPublicList ?? []
  );
}

export async function listOrganizationUsersFiltered(filter?: {
  organizationId?: { eq?: string };
  accountStatus?: { ne?: string };
}) {
  const session = await client.graphql({
    query: listOrganizationUsersFilteredQuery,
    variables: { filter }
  });

  return (
    (
      session as unknown as {
        data?: { listOrganizationUsersFiltered?: any[] };
      }
    ).data?.listOrganizationUsersFiltered ?? []
  );
}

export async function inviteUser(input: UserInviteInput) {
  const session = await client.graphql<GraphQLQuery<OrganizationInviteUserMutation>>({
    query: organizationInviteUser,
    variables: { input }
  });

  return session.data?.organizationInviteUser;
}

export async function resendUserInvite(email: string, isAdmin: boolean) {
  const session = await client.graphql<GraphQLQuery<UserResendInviteMutation>>({
    query: userResendInvite,
    variables: { email, isAdmin }
  });

  return session.data?.userResendInvite;
}

export async function deleteUser(id: string) {
  const session = await client.graphql<GraphQLQuery<UserDeleteMutation>>({
    query: userDelete,
    variables: { id }
  });

  return session.data?.userDelete;
}
